import { Modal, notification } from "antd";
import { get, isEmpty, isNil, noop } from "lodash";
import { useState } from "react";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { API_ERRORS, apiErrorCode } from "../../api-errors";
import { contentOfTemplate, draftState } from "./draft-content";
import { saveBytes } from "./pdf-reader";
import { SAVE_OUTCOMES } from "./use-template-draft";

const useDraftCommands = ({
  template: { id },
  live,
  draft,
  zoneSource,
  reload,
  reloadAfterConflict,
  closePanel,
  closeSheet,
  t,
}) => {
  const [loadFileUrl] = useLazyQuery(graphql.queries.DOCUMENT_TEMPLATE_FILE, {
    fetchPolicy: "network-only",
    onError: noop,
  });
  const [publishTemplate] = useMutation(
    graphql.mutations.PUBLISH_DOCUMENT_TEMPLATE,
    { onError: noop },
  );
  const [discardDraft] = useMutation(
    graphql.mutations.DISCARD_DOCUMENT_TEMPLATE_DRAFT,
    { onError: noop },
  );
  const [restoreVersion] = useMutation(
    graphql.mutations.RESTORE_DOCUMENT_TEMPLATE_VERSION,
    { onError: noop },
  );
  const [publishing, setPublishing] = useState(false);
  const { pendingFile, reset, hold, persist, readRevision, replaceFile } =
    draft;

  const publish = async () => {
    setPublishing(true);
    const outcome = await hold(async () => {
      const saved = await persist({ force: isNil(readRevision()) });
      if (saved === SAVE_OUTCOMES.CONFLICT || saved === SAVE_OUTCOMES.FAILED)
        return { saved };
      const result = await publishTemplate({
        variables: { where: { id }, revision: readRevision() },
      });
      return { saved, result };
    });
    setPublishing(false);
    if (outcome.saved === SAVE_OUTCOMES.CONFLICT) {
      closeSheet();
      await draft.refresh();
      return;
    }
    if (outcome.saved === SAVE_OUTCOMES.FAILED) {
      notification.error({ message: t("PUBLISH.FAILED") });
      return;
    }
    const published = get(outcome.result, "data.publishDocumentTemplate");
    if (isNil(published)) {
      const code = apiErrorCode(get(outcome.result, "errors"));
      if (code !== API_ERRORS.CONFLICT && code !== API_ERRORS.NOT_FOUND) {
        notification.error({ message: t("PUBLISH.FAILED") });
        return;
      }
      closeSheet();
      await reload().catch(() => false);
      notification.warning({
        message: t(
          code === API_ERRORS.CONFLICT ? "PUBLISH.STALE" : "PUBLISH.NO_DRAFT",
        ),
      });
      return;
    }
    const reloaded = await reload().catch(() => false);
    if (!reloaded)
      reset({
        content: contentOfTemplate(published),
        revision: null,
        pendingFile: null,
      });
    closePanel();
    closeSheet();
    notification.success({ message: t("PUBLISH.DONE") });
  };

  const discard = () =>
    Modal.confirm({
      title: t("DISCARD.TITLE"),
      content: t("DISCARD.CONTENT"),
      okText: t("DISCARD.CONFIRM"),
      cancelText: t("DISCARD.CANCEL"),
      okButtonProps: { danger: true },
      onOk: async () => {
        const result = await hold(() =>
          discardDraft({ variables: { where: { id } } }),
        );
        if (get(result, "data.discardDocumentTemplateDraft") !== true) {
          notification.error({ message: t("DISCARD.FAILED") });
          return;
        }
        const reloaded = await reload().catch(() => false);
        if (reloaded) notification.success({ message: t("DISCARD.DONE") });
        else notification.error({ message: t("HEADER.RELOAD_FAILED") });
      },
    });

  const restore = async (version) => {
    const result = await hold(() =>
      restoreVersion({
        variables: { where: { id: version.id }, revision: readRevision() },
      }),
    );
    const row = get(result, "data.restoreDocumentTemplateVersion");
    if (isNil(row)) {
      if (apiErrorCode(get(result, "errors")) !== API_ERRORS.CONFLICT) {
        notification.error({ message: t("HISTORY.RESTORE_FAILED") });
        return;
      }
      closeSheet();
      await reloadAfterConflict();
      return;
    }
    reset(draftState({ draft: row, template: live }));
    closePanel();
    closeSheet();
    notification.success({
      message: t("HISTORY.RESTORED", { number: version.versionNumber }),
    });
  };

  const applyNewFile = async ({ file, apply }) => {
    const outcome = await replaceFile({ file, apply });
    if (outcome === SAVE_OUTCOMES.CONFLICT) return false;
    if (outcome === SAVE_OUTCOMES.FAILED) {
      notification.error({ message: t("REPLACE_PDF.SAVE_FAILED") });
      return false;
    }
    closePanel();
    notification.success({ message: t("REPLACE_PDF.DONE") });
    return true;
  };

  const downloadOriginal = async () => {
    if (!isNil(pendingFile)) {
      saveBytes({
        bytes: zoneSource.source.bytes,
        fileName: pendingFile.name || zoneSource.source.name,
        contentType: zoneSource.source.contentType,
      });
      return;
    }
    const tab = window.open("", "_blank");
    const result = await loadFileUrl({ variables: { where: { id } } });
    const url = get(result, "data.documentTemplateFile");
    if (isEmpty(url)) {
      tab?.close();
      notification.error({ message: t("MENU.DOWNLOAD_FAILED") });
      return;
    }
    if (isNil(tab)) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    tab.opener = null;
    tab.location.href = url;
  };

  return {
    publishing,
    publish,
    discard,
    restore,
    applyNewFile,
    downloadOriginal,
  };
};

export default useDraftCommands;
